import { useEffect, useRef, useState } from 'react'
import { ChangeEvent } from 'react'
import { InputLabel, InputWrapper, Required, SuffixIcon, ValidateText } from './styles'
import { Field, FieldProps, FormikHandlers } from 'formik'
import { Input as AInput } from 'antd'
import _ from 'lodash/fp'
import CrossIcon from './assets/cross.svg'
import CheckIcon from './assets/check.svg'
import Indication from 'components/Indication'
import { isMobile } from 'react-device-detect'

interface IProps {
  name: string,
  type?: 'text' | 'password' | 'multiline' | 'email',
  placeholder?: string,
  indication?: string,
  submitOnEnter?: boolean,
  hideError?: boolean,
  noBottomPadding?: boolean,
  nbPerLine?: number,
  defaultFocus?: boolean,
  disabled?: boolean,
  onChange?: (value: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => void,
  showErrorOnlyIfEntered?: boolean,
  bottomPadding?: number,
  bottomPaddingWhenError?: number,
  onlyNumeric?: boolean,
  formatter?: (value: string) => string,
  label?: string,
  rows?: number,
  maxLength?: number,
  spellcheck?: boolean,
  required?: boolean,
  validateText?: string,
  validateEnabledRegexp?: RegExp,
  defaultValue?: string | number
}

const Input = (props: IProps) => {
  const { name, type, placeholder, indication, hideError, noBottomPadding,
    nbPerLine, defaultFocus, disabled, onChange, showErrorOnlyIfEntered,
    bottomPadding, bottomPaddingWhenError, onlyNumeric, formatter, label, rows,
    maxLength, spellcheck, required, validateText, validateEnabledRegexp,
    defaultValue } = props
  const [entered, setEntered] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef && inputRef.current && defaultFocus && !isMobile) {
      inputRef.current.focus()
    }
  }, [defaultFocus])

  return (
    <Field
      name={name}
    >
      {(props: FieldProps) => {
        const isValid = !props.form.errors[name] && (_.isNil(props.field.value) || props.field.value.trim() !== '')
        const hasError = !!props.form.errors[name] && (!showErrorOnlyIfEntered || entered)

        return (
          <InputWrapper
            className="ant-input-wrapper"
            hasError={hasError}
            nbPerLine={nbPerLine || 1}
            noBottomPadding={noBottomPadding}
            bottomPadding={(bottomPaddingWhenError && hasError) ? bottomPaddingWhenError : bottomPadding}
          >
            {label &&
              <InputLabel>
                {required &&
                  <Required>* </Required>
                }

                {label}
              </InputLabel>
            }

            {type === 'multiline' ?
              <AInput.TextArea
                name={name}
                placeholder={placeholder}
                value={formatter ? formatter(props.field.value) : props.field.value}
                disabled={disabled}
                rows={rows}
                onChange={change(props.field.onChange, setEntered, onChange, onlyNumeric)}
                onBlur={props.field.onBlur}
                maxLength={maxLength}
                spellCheck={spellcheck}
                ref={(inputRef as any)}
              /> :
              <AInput
                defaultValue={defaultValue}
                name={name}
                type={type}
                placeholder={placeholder}
                value={formatter ? formatter(props.field.value) : props.field.value}
                disabled={disabled}
                onChange={change(props.field.onChange, setEntered, onChange, onlyNumeric)}
                onBlur={props.field.onBlur}
                maxLength={maxLength}
                spellCheck={spellcheck}
                suffix={
                  validateText ?
                    <ValidateText
                      disabled={validateEnabledRegexp && !validateEnabledRegexp.test(props.field.value || '')}
                      onClick={() => {
                        if (props.field.value &&
                          (validateEnabledRegexp == null || validateEnabledRegexp.test(props.field.value))) {
                          props.form.submitForm()
                        }
                      }}
                    >
                      {validateText}
                    </ValidateText> :
                    (!showErrorOnlyIfEntered || entered) ?
                      <SuffixIcon
                        alt={'state-' + name}
                        src={isValid ? CheckIcon : CrossIcon}
                      /> :
                      <span />
                }
                ref={(inputRef as any)}
              />
            }

            {(indication || (props.form.errors[name] && !hideError)) &&
              <Indication
                hasError={hasError}
              >
                {
                  (props.form.errors[name] && (!showErrorOnlyIfEntered || entered)) ?
                    props.form.errors[name] :
                    indication
                }
              </Indication>
            }
          </InputWrapper>
        )
      }}
    </Field>
  )
}

const change = (
    fieldOnChange: FormikHandlers['handleChange'],
    setEntered: (entered: boolean) => void,
    onChange?: (value: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => void,
    onlyNumeric?: boolean
) => (event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => {
  const numberRegex = /^([0-9 +()\-)])*$/

  if (!onlyNumeric || numberRegex.test(event.target.value)) {
    if (onChange) {
      onChange(event)
    }

    setEntered(true)

    fieldOnChange(event)
  }
}

Input.defaultProps = {
  type: 'text',
  hideError: false,
  noBottomPadding: false,
  nbPerLine: 1,
  defaultFocus: false,
  disabled: false,
  showErrorOnlyIfEntered: true,
  bottomPadding: 17,
  onlyNumeric: false,
  rows: 4,
  spellcheck: true
}

export default Input
